// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the layer stack's overlays on the immersive globe: one thin
 * transparent shell per overlay (country borders, coastlines, grids,
 * labels), drawn just outside the globe surface over whatever the
 * dataset shows. The browser globe draws the same overlays as passes in
 * earthTileLayer; `mapLayers.ts` decides which.
 *
 * The shells are children of the globe mesh, so they follow its
 * position, rotation and placement scale with no per-frame work. The
 * material is vrBorders' — premultiplied alpha, depth-tested so lines on
 * the far side of the sphere stay hidden, double-sided for a user who
 * steps inside — plus a tint that redraws line art in white or black.
 *
 * Each shell has a twin just inside the surface. The globe draws its
 * inner face too, which hides the outer shell from a viewer inside; the
 * twin is what they see, mirrored east-west as the surface is.
 */

import type * as THREE from 'three'
import type { MapLayerTint } from './earthTileLayer'
import { SPARSE_PICK_GLSL, firstDrawnCode } from './sparseSampling'
import { COLOR_KEY_GLSL, colorKeyUniform, sameColorKey, type ColorKey } from './colorKey'
import { FOCUS_ZOOM_GLSL, focusZoomUniforms } from './vrFocusZoom'

/** Just outside vrBorders' 1.001 shell, then a hair further out per layer. */
const BASE_RADIUS_FACTOR = 1.0015
const STEP_RADIUS_FACTOR = 0.0005
const SEGMENTS = 64
const TINT_CODE: Record<MapLayerTint, number> = { source: 0, white: 1, black: 2 }

export interface VrMapOverlay {
  /** A picture, or a real-time overlay stream's video (a VideoTexture keeps it current). */
  readonly image: HTMLImageElement | HTMLCanvasElement | HTMLVideoElement
  readonly tint: MapLayerTint
  readonly opacity?: number
  /**
   * A value-encoded stream's 256-entry RGBA palette, indexed by the raw
   * luma code, and the map's rectangle in the frame in image space (rows
   * from the top), as earthTileLayer takes them. Absent for a picture.
   */
  readonly lut?: Uint8Array
  readonly crop?: { u0: number; v0: number; us: number; vs: number }
  /** AYNI: a class map elsewhere in the frame (IMERG's rain/snow), as earthTileLayer takes it. */
  readonly phase?: { classMap: Uint8Array; crop: { u0: number; v0: number; us: number; vs: number }; rows: number }
  /** AYNI: a picture stream's colour key (colorKey.ts): that colour drawn clear. Ignored with `lut`. */
  readonly colorKey?: ColorKey
  /** Asked every frame; false draws nothing (a real-time overlay outside its dates). */
  readonly visible?: () => boolean
  /** AYNI (owner, 2026-10-12): asked every frame, the opacity the viewer set (its transparency); multiplies `opacity`. */
  readonly userOpacity?: () => number
}

export interface VrMapOverlaysHandle {
  /** Replace the overlays, bottom first. An empty list removes them all. */
  set(overlays: readonly VrMapOverlay[]): void
  dispose(): void
}

export function createVrMapOverlays(
  THREE_: typeof THREE,
  globe: THREE.Mesh,
  globeRadius: number,
): VrMapOverlaysHandle {
  let shells: Array<{
    mesh: THREE.Mesh
    texture: THREE.Texture
    lutTexture: THREE.DataTexture | null
    /** AYNI: the class map's code-to-row table (IMERG's rain/snow), or none. */
    phaseTexture: THREE.DataTexture | null
    material: THREE.ShaderMaterial
    geometry: THREE.SphereGeometry
  }> = []
  let current: readonly VrMapOverlay[] = []

  function clear(): void {
    for (const s of shells) {
      globe.remove(s.mesh)
      s.texture.dispose()
      s.lutTexture?.dispose()
      s.phaseTexture?.dispose()
      s.material.dispose()
      s.geometry.dispose()
    }
    shells = []
  }

  function shell(overlay: VrMapOverlay, index: number) {
    const texture = overlay.image instanceof HTMLVideoElement
      ? new THREE_.VideoTexture(overlay.image)
      : new THREE_.Texture(overlay.image)
    let lutTexture: THREE.DataTexture | null = null
    if (overlay.lut) {
      // Codes, not colours: no colour-space decode and no filtering that
      // would average neighbouring codes into a value nobody measured.
      texture.colorSpace = THREE_.NoColorSpace
      texture.minFilter = THREE_.NearestFilter
      texture.magFilter = THREE_.NearestFilter
      texture.generateMipmaps = false
      lutTexture = new THREE_.DataTexture(overlay.lut, 256, Math.max(1, Math.floor(overlay.lut.length / 1024)), THREE_.RGBAFormat)
      lutTexture.colorSpace = THREE_.SRGBColorSpace
      lutTexture.minFilter = THREE_.NearestFilter
      lutTexture.magFilter = THREE_.NearestFilter
      lutTexture.needsUpdate = true
    } else {
      texture.colorSpace = THREE_.SRGBColorSpace
      texture.anisotropy = 4
    }
    // AYNI: numbers, not colours, so no sRGB decode (releasePhase.ts).
    let phaseTexture: THREE.DataTexture | null = null
    const phase = lutTexture ? overlay.phase : undefined
    if (phase) {
      phaseTexture = new THREE_.DataTexture(phase.classMap, 256, 1, THREE_.RGBAFormat)
      phaseTexture.colorSpace = THREE_.NoColorSpace
      phaseTexture.minFilter = THREE_.NearestFilter
      phaseTexture.magFilter = THREE_.NearestFilter
      phaseTexture.needsUpdate = true
    }
    const phaseRegion = phase
      ? new THREE_.Vector4(phase.crop.u0, 1 - (phase.crop.v0 + phase.crop.vs), phase.crop.us, phase.crop.vs)
      : new THREE_.Vector4(0, 0, 0, 0)
    texture.needsUpdate = true
    // THREE flips Y on upload, so v counts from the bottom of the frame.
    const crop = overlay.crop
    const region = crop
      ? new THREE_.Vector4(crop.u0, 1 - (crop.v0 + crop.vs), crop.us, crop.vs)
      : new THREE_.Vector4(0, 0, 1, 1)
    const material = new THREE_.ShaderMaterial({
      uniforms: {
        uMap: { value: texture },
        uTint: { value: TINT_CODE[overlay.tint] },
        uOpacity: { value: overlay.opacity ?? 1 },
        uEncoded: { value: lutTexture !== null },
        uLut: { value: lutTexture },
        uRegion: { value: region },
        uPhaseRegion: { value: phaseRegion },
        uPhaseMap: { value: phaseTexture },
        uLutRows: { value: phase?.rows ?? 1 },
        uSparseCutoff: { value: lutTexture ? firstDrawnCode(overlay.lut) : 0 },
        uColorKey: { value: new THREE_.Vector4(...colorKeyUniform(lutTexture ? null : overlay.colorKey)) },
        // AYNI (2026-10-22): the headset's zoom (vrFocusZoom.ts), shared by every globe material.
        ...focusZoomUniforms,
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        precision highp float;
        uniform sampler2D uMap;
        uniform int uTint;
        uniform float uOpacity;
        uniform bool uEncoded;
        uniform sampler2D uLut;
        uniform vec4 uRegion;
        // AYNI: a class map (IMERG's rain/snow): its rectangle (zw == 0: none),
        // its code-to-row table, and the palette rows uLut then has.
        uniform vec4 uPhaseRegion;
        uniform sampler2D uPhaseMap;
        uniform float uLutRows;
        // AYNI: first drawn code; below it a pixel looks for lone data texels (sparseSampling.ts).
        uniform float uSparseCutoff;
        // AYNI: a picture stream's colour key, w < 0 for none (colorKey.ts).
        uniform vec4 uColorKey;
        varying vec2 vUv;
        ${SPARSE_PICK_GLSL}
        ${COLOR_KEY_GLSL}
        ${FOCUS_ZOOM_GLSL}
        void main() {
          vec2 flatUv = vec2(gl_FrontFacing ? vUv.x : 1.0 - vUv.x, vUv.y);
          // AYNI (2026-10-22): the place drawn here through the headset's zoom.
          vec2 local = zoomUv(flatUv, zoomLatLon(flatUv));
          vec2 localFw = uZoomM > 1.0 ? zoomFootprint(local) : sparseFootprint(vUv);
          vec2 uv = uRegion.xy + local * uRegion.zw;
          vec4 tex;
          if (uEncoded) {
            local = sparsePick(uMap, uRegion, local, localFw, uSparseCutoff);
            uv = uRegion.xy + local * uRegion.zw;
            float code = floor(texture2D(uMap, uv).r * 255.0 + 0.5);
            float row = 0.0;
            if (uPhaseRegion.z > 0.0) {
              // Kept off the class map's edge rows, which border other parts of the frame.
              vec2 phaseUv = uPhaseRegion.xy + clamp(local, 0.0005, 0.9995) * uPhaseRegion.zw;
              float phaseCode = floor(texture2D(uMap, phaseUv).r * 255.0 + 0.5);
              row = floor(texture2D(uPhaseMap, vec2((phaseCode + 0.5) / 256.0, 0.5)).r * 255.0 + 0.5);
            }
            tex = texture2D(uLut, vec2((code + 0.5) / 256.0, (row + 0.5) / max(uLutRows, 1.0)));
          } else {
            tex = zoomTexture(uMap, uv);
            if (uColorKey.w >= 0.0) tex.a *= colorKeyAlphaLinear(tex.rgb, uColorKey);
          }
          float a = tex.a * uOpacity;
          if (a < 0.01) discard;
          vec3 rgb = uTint == 1 ? vec3(1.0) : (uTint == 2 ? vec3(0.0) : tex.rgb);
          gl_FragColor = vec4(rgb * a, a);
        }
      `,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE_.CustomBlending,
      blendEquation: THREE_.AddEquation,
      blendSrc: THREE_.OneFactor,
      blendDst: THREE_.OneMinusSrcAlphaFactor,
      premultipliedAlpha: true,
      side: THREE_.DoubleSide,
    })
    const radiusFactor = BASE_RADIUS_FACTOR + index * STEP_RADIUS_FACTOR
    const geometry = new THREE_.SphereGeometry(globeRadius * radiusFactor, SEGMENTS, SEGMENTS)
    const mesh = new THREE_.Mesh(geometry, material)
    // The twin: as far inside the surface as the shell is outside it.
    const interior = new THREE_.Mesh(geometry, material)
    interior.scale.setScalar((2 - radiusFactor) / radiusFactor)
    mesh.add(interior)
    const visible = overlay.visible
    const own = overlay.userOpacity
    if (visible || own) {
      const opacity = overlay.opacity ?? 1
      // Zero opacity rather than mesh.visible, which would also stop this
      // hook from running to turn it back on. The shader discards it all.
      mesh.onBeforeRender = () => {
        material.uniforms.uOpacity!.value = !visible || visible() ? opacity * Math.min(1, Math.max(0, own?.() ?? 1)) : 0
      }
    }
    // After the globe and the borders shell, in stack order.
    mesh.renderOrder = 2 + index
    interior.renderOrder = mesh.renderOrder
    globe.add(mesh)
    return { mesh, texture, lutTexture, phaseTexture, material, geometry }
  }

  return {
    set(overlays) {
      const same = overlays.length === current.length &&
        overlays.every((o, i) => {
          const c = current[i]!
          return o.image === c.image && o.tint === c.tint && (o.opacity ?? 1) === (c.opacity ?? 1) && o.lut === c.lut && sameColorKey(o.colorKey, c.colorKey)
        })
      if (same) return
      current = overlays
      clear()
      shells = overlays.map(shell)
    },
    dispose() {
      clear()
      current = []
    },
  }
}
